import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Popconfirm, Table } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useDeleteStaffWorkday, useStaffWorkdays } from "../hooks/useStaffWorkday";
import { useStaffWorkdayStore } from "../store/staff-workday.store";
import type { StaffWorkday } from "../types";

const DAY_NAMES: Record<number, string> = {
	1: "Dushanba",
	2: "Seshanba",
	3: "Chorshanba",
	4: "Payshanba",
	5: "Juma",
	6: "Shanba",
	7: "Yakshanba",
};

export function StaffWorkdayTable() {
	const { onOpen } = useStaffWorkdayStore();
	const { page: currentPage, limit: pageSize, setPage } = useUrlTableState();
	const { data, isLoading } = useStaffWorkdays();
	const { mutate: remove } = useDeleteStaffWorkday();

	const items = data ?? [];
	const total = items.length;
	const paginatedItems = items.slice((currentPage - 1) * pageSize, currentPage * pageSize);

	return (
		<Table
			loading={isLoading}
			pagination={tablePagination({
				page: currentPage,
				limit: pageSize,
				total,
				onChange: (page, size) => setPage(page, size ?? pageSize),
			})}
			size="small"
			scroll={{ x: "max-content" }}
			dataSource={paginatedItems}
			rowKey="id"
			locale={{
				emptyText: <EmptyState description="Ish kunlari topilmadi" />,
			}}
			columns={[
				{
					key: "#",
					title: "№",
					width: 60,
					align: "center",
					render: (_, __, index) => formatTableIndex(currentPage, pageSize, index),
				},
				{
					key: "dayNumber",
					title: "Kun",
					dataIndex: "dayNumber",
					width: 130,
					render: (dayNumber: number) => DAY_NAMES[dayNumber] ?? dayNumber,
				},
				{
					key: "staffId",
					title: "Xodim ID",
					dataIndex: "staffId",
					ellipsis: true,
				},
				{
					key: "start",
					title: "Boshlanish",
					dataIndex: "start",
					width: 110,
					render: (start: string) => <span className="font-mono">{start}</span>,
				},
				{
					key: "end",
					title: "Tugash",
					dataIndex: "end",
					width: 110,
					render: (end: string) => <span className="font-mono">{end}</span>,
				},
				{
					key: "isWorkday",
					title: "Ish kuni",
					dataIndex: "isWorkday",
					width: 100,
					align: "center",
					render: (isWorkday: boolean) =>
						isWorkday ? (
							<StatusTag tone="success">Ha</StatusTag>
						) : (
							<StatusTag tone="error">Yo&apos;q</StatusTag>
						),
				},
				{
					key: "actions",
					title: "Amallar",
					width: 80,
					align: "center",
					render: (_: unknown, record: StaffWorkday) => (
						<Dropdown
							menu={{
								items: [
									{
										key: "edit",
										icon: <EditOutlined />,
										label: "Tahrirlash",
										onClick: () => onOpen(record),
									},
									{
										key: "delete",
										danger: true,
										label: (
											// biome-ignore lint/a11y/useKeyWithClickEvents: dropdown menu item
											// biome-ignore lint/a11y/noStaticElementInteractions: dropdown menu item
											<div onClick={(e) => e.stopPropagation()}>
												<Popconfirm
													title="O'chirish"
													description="Rostdan ham o'chirmoqchimisiz?"
													onConfirm={() => remove(record.id)}
													okText="Ha"
													cancelText="Yo'q"
													okButtonProps={{ danger: true }}
												>
													<div
														style={{
															width: "100%",
															display: "flex",
															alignItems: "center",
															gap: "8px",
														}}
													>
														<DeleteOutlined />
														<span>O&apos;chirish</span>
													</div>
												</Popconfirm>
											</div>
										),
									},
								],
							}}
							trigger={["click"]}
							placement="bottomRight"
						>
							<Button type="text" icon={<MoreOutlined />} size="small" />
						</Dropdown>
					),
				},
			]}
		/>
	);
}
